import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Tv, Trophy, Clock, AlertCircle, ExternalLink, Sparkles, Crown } from "lucide-react";
import { SorteoLiveVotingPanel } from "@/components/sorteo/SorteoLiveVotingPanel";

/**
 * Large overlay modal for the Live Sorteo experience.
 *
 *   ┌──────────────────────────────────────────────┐
 *   │  TikTok Live (left)   │   Wheel + status     │
 *   │                       │   Winners of the day │
 *   └──────────────────────────────────────────────┘
 *
 * Voting is intentionally NOT enabled from this overlay — the user is
 * informed of the sorteo's status (voting / spinning / not started) and
 * can vote via the existing SorteoFloat dialog. This modal is the public
 * "viewing window" while watching the TikTok live broadcast.
 */
export const SorteoLiveModal = ({ state, onClose, onRefresh }) => {
  const status = state?.sorteo?.status || null;
  const tiktokUrl = state?.tiktok_live_url || "https://www.tiktok.com/@crisangelastral/live";
  const winners = state?.winners_today || [];
  const count = state?.winners_today_count || 0;

  // Local countdown timer (refreshed every second when voting is open)
  const initialSeconds = state?.time_remaining_seconds;
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);
  useEffect(() => { setSecondsLeft(state?.time_remaining_seconds); }, [state?.time_remaining_seconds]);
  useEffect(() => {
    if (status !== "voting" || secondsLeft == null) return;
    const id = setInterval(() => {
      setSecondsLeft((s) => {
        if (s == null) return s;
        if (s <= 1) { onRefresh?.(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [status, secondsLeft == null, onRefresh]);

  // TikTok embed URL — handle/path → embed/live form. Fall back to the raw URL.
  const tiktokEmbed = useMemo(() => {
    try {
      // e.g. https://www.tiktok.com/@crisangelastral/live → https://www.tiktok.com/embed/live/@crisangelastral
      const m = tiktokUrl.match(/tiktok\.com\/(@[^/]+)\/live/i);
      if (m) return `https://www.tiktok.com/embed/live/${m[1]}`;
    } catch { /* ignore */ }
    return tiktokUrl;
  }, [tiktokUrl]);

  const mm = secondsLeft != null ? String(Math.floor(secondsLeft / 60)).padStart(2, "0") : "--";
  const ss = secondsLeft != null ? String(secondsLeft % 60).padStart(2, "0") : "--";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[150] bg-black/90 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="sorteo-live-modal"
    >
      <motion.div
        initial={{ y: 28, scale: 0.96, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: 16, scale: 0.97, opacity: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-6xl my-4 rounded-3xl overflow-hidden border border-rose-500/40 shadow-[0_0_80px_rgba(244,63,94,0.3)]"
        style={{
          background:
            "radial-gradient(circle at 0% 0%, rgba(180,30,80,0.35) 0%, transparent 55%), radial-gradient(circle at 100% 100%, rgba(212,175,55,0.18) 0%, transparent 55%), linear-gradient(180deg, #0c0612 0%, #050307 100%)",
        }}
      >
        {/* Close */}
        <button
          type="button"
          data-testid="sorteo-live-close"
          onClick={onClose}
          aria-label="Cerrar"
          className="absolute top-3 right-3 z-30 w-9 h-9 rounded-full bg-black/60 hover:bg-black/90 border border-white/15 text-neutral-200 hover:text-white flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-3 border-b border-white/5 flex items-center gap-2.5">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
          <span className="text-[10px] uppercase tracking-[0.35em] text-red-300 font-bold">En vivo · TikTok + Ruleta</span>
          <Tv className="w-4 h-4 text-rose-400 ml-1" />
        </div>

        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-0">
          {/* LEFT — TikTok Live */}
          <div className="relative border-b lg:border-b-0 lg:border-r border-white/5 bg-black/40 min-h-[420px]">
            <div className="aspect-[9/16] w-full max-h-[78vh] relative">
              <iframe
                data-testid="tiktok-live-iframe"
                src={tiktokEmbed}
                title="TikTok Live de CrisAngel Astral"
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
            <a
              href={tiktokUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="tiktok-open-link"
              className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-rose-200 hover:text-white bg-black/70 border border-rose-400/40 rounded-full px-3 py-1.5"
            >
              <ExternalLink className="w-3 h-3" /> Abrir en TikTok
            </a>

            {/* Voting wheel — floats IN FRONT of the TikTok box during voting. */}
            <AnimatePresence>
              {status === "voting" && (
                <SorteoLiveVotingPanel
                  key="live-voting"
                  sorteoSummary={state?.sorteo}
                  secondsLeft={secondsLeft}
                />
              )}
            </AnimatePresence>
          </div>

          {/* RIGHT — Sorteo status + winners */}
          <div className="p-5 sm:p-6 lg:p-7 flex flex-col">
            {/* Status banner */}
            {status === "voting" && (
              <div data-testid="sorteo-status-voting" className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-4">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-1">
                  ✓ Votación abierta
                </div>
                <h3 className="font-serif text-lg text-white">{state?.sorteo?.title || "Sorteo en curso"}</h3>
                <p className="text-xs text-neutral-300 mt-1 leading-relaxed">
                  Vota desde la ruleta que apareció en frente del live (lado izquierdo).
                </p>
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-emerald-300" />
                  <span className="text-neutral-400">Tiempo restante:</span>
                  <span data-testid="sorteo-time-remaining" className="font-mono text-2xl text-emerald-200 font-bold">{mm}:{ss}</span>
                </div>
              </div>
            )}

            {status === "spinning" && (
              <div data-testid="sorteo-status-spinning" className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4">
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-1">
                  🎡 La ruleta está girando
                </div>
                <h3 className="font-serif text-lg text-white">{state?.sorteo?.title || "Girando..."}</h3>
                <p className="text-xs text-neutral-300 mt-1 leading-relaxed">
                  ¡El destino está decidiendo! Mira el resultado en el botón de Sorteo de la pantalla.
                </p>
              </div>
            )}

            {!status && (
              <div data-testid="sorteo-status-idle" className="rounded-2xl border border-red-400/30 bg-red-500/10 p-4">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-red-300 font-bold mb-1">
                  <AlertCircle className="w-4 h-4" /> Aún no comienza el sorteo
                </div>
                <p className="text-xs text-neutral-300 mt-1 leading-relaxed">
                  El maestro Cristopher aún no inicia la ruleta. Quédate viendo el live de TikTok y te avisaremos en cuanto la votación esté abierta.
                </p>
              </div>
            )}

            {/* Winners of the day */}
            <div className="mt-5 flex-1 flex flex-col min-h-0">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-[#d4af37]" />
                  <h4 className="font-serif text-base text-white">Ganadores del día</h4>
                </div>
                <span
                  data-testid="winners-today-count"
                  className="text-[10px] uppercase tracking-widest bg-[#d4af37] text-black px-2 py-0.5 rounded-full font-bold"
                >
                  {count}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto rounded-xl border border-white/5 bg-black/30 max-h-[280px]">
                {count === 0 ? (
                  <div className="p-6 text-center text-xs text-neutral-500 italic" data-testid="winners-today-empty">
                    Aún no hay ganadores en el día de hoy.
                  </div>
                ) : (
                  <ul className="divide-y divide-white/5">
                    {winners.map((w, idx) => {
                      const winnerName = (w.winner && (w.winner.name || w.winner.username)) || "Ganador";
                      const visibleId = w.winner && w.winner.visible_id;
                      return (
                        <li key={`${w.sorteo_id}-${idx}`} className="px-3 py-2.5 flex items-center gap-3" data-testid={`winner-row-${idx}`}>
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#d4af37] to-amber-700 flex items-center justify-center text-black font-bold text-xs flex-shrink-0">
                            <Crown className="w-4 h-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-white font-semibold truncate">{winnerName}</div>
                            <div className="text-[10px] text-neutral-500 flex items-center gap-1.5">
                              {visibleId && <span className="font-mono">#{visibleId}</span>}
                              <Sparkles className="w-2.5 h-2.5 text-[#d4af37]" />
                              <span className="truncate">{w.prize || w.title}</span>
                            </div>
                          </div>
                          <div className="text-[10px] text-neutral-500 whitespace-nowrap">
                            {w.completed_at ? new Date(w.completed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>

            <p className="text-[10px] text-neutral-500 text-center mt-3">
              Durante la votación, la ruleta aparece sobre el live para votar al instante.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
